import React, { useState } from 'react';
import { Copy, Download, RotateCcw, Coffee, Check, Settings, FileText } from 'lucide-react';

interface JavaFormatterOptions {
  indentSize: number;
  useTabs: boolean;
  insertFinalNewline: boolean;
  trimTrailingWhitespace: boolean;
  insertSpaceAfterComma: boolean;
  insertSpaceBeforeBrace: boolean;
  insertSpaceAfterKeyword: boolean;
  insertSpaceAroundOperators: boolean;
  braceStyle: 'K&R' | 'Allman' | 'GNU' | 'Horstmann';
  maxLineLength: number;
  sortImports: boolean;
  removeExtraBlankLines: boolean;
  alignParameters: boolean;
}

const JavaFormatter: React.FC = () => {
  const [inputCode, setInputCode] = useState<string>('');
  const [formattedCode, setFormattedCode] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [showStats, setShowStats] = useState<boolean>(false);
  const [options, setOptions] = useState<JavaFormatterOptions>({
    indentSize: 4,
    useTabs: false,
    insertFinalNewline: true,
    trimTrailingWhitespace: true,
    insertSpaceAfterComma: true,
    insertSpaceBeforeBrace: true,
    insertSpaceAfterKeyword: true,
    insertSpaceAroundOperators: true,
    braceStyle: 'K&R',
    maxLineLength: 120,
    sortImports: true,
    removeExtraBlankLines: true,
    alignParameters: false,
  });

  const formatJava = (code: string): void => {
    if (!code.trim()) {
      setFormattedCode('');
      return;
    }

    try {
      let result = code;
      
      // Extract and preserve string literals and comments
      const strings: string[] = [];
      const comments: string[] = [];
      
      // Extract string literals
      result = result.replace(/"(?:[^"\\]|\\.)*"/g, (match) => {
        strings.push(match);
        return `__STRING_${strings.length - 1}__`;
      });
      
      // Extract comments
      result = result.replace(/\/\*[\s\S]*?\*\//g, (match) => {
        comments.push(match);
        return `__BLOCK_COMMENT_${comments.length - 1}__`;
      });
      
      result = result.replace(/\/\/.*$/gm, (match) => {
        comments.push(match);
        return `__LINE_COMMENT_${comments.length - 1}__`;
      });
      
      // Process imports
      if (options.sortImports) {
        const importLines: string[] = [];
        const otherLines: string[] = [];
        
        result.split('\n').forEach(line => {
          if (line.trim().startsWith('import ')) {
            importLines.push(line.trim());
          } else {
            otherLines.push(line);
          }
        });
        
        importLines.sort();
        result = [...importLines, '', ...otherLines].join('\n');
      }
      
      // Remove extra whitespace
      result = result.replace(/[ \t]+/g, ' ');
      
      // Format the code structure
      result = formatCodeStructure(result);
      
      // Apply brace style
      result = applyBraceStyle(result);
      
      // Add proper spacing
      result = addSpacing(result);
      
      // Remove extra blank lines
      if (options.removeExtraBlankLines) {
        result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
      }
      
      // Restore strings and comments
      strings.forEach((str, index) => {
        result = result.replace(`__STRING_${index}__`, str);
      });
      
      comments.forEach((comment, index) => {
        if (comment.startsWith('//')) {
          result = result.replace(`__LINE_COMMENT_${index}__`, comment);
        } else {
          result = result.replace(`__BLOCK_COMMENT_${index}__`, comment);
        }
      });
      
      // Final cleanup
      if (options.trimTrailingWhitespace) {
        result = result.replace(/[ \t]+$/gm, '');
      }
      
      if (options.insertFinalNewline && !result.endsWith('\n')) {
        result += '\n';
      }
      
      setFormattedCode(result.trim());
    } catch (error) {
      setFormattedCode(`Error formatting Java code: ${(error as Error).message}`);
    }
  };

  const formatCodeStructure = (code: string): string => {
    let result = '';
    let indent = 0;
    let inAnnotation = false;
    
    const lines = code.split('\n');
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      
      if (!line) {
        result += '\n';
        continue;
      }
      
      // Handle annotations
      if (line.startsWith('@')) {
        inAnnotation = true;
        result += getIndentation(indent) + line + '\n';
        continue;
      }
      
      // Handle class/interface/enum declarations
      if (line.match(/^(public|private|protected)?\s*(class|interface|enum|abstract)\s+/)) {
        if (inAnnotation) {
          inAnnotation = false;
        } else {
          result += '\n';
        }
        result += getIndentation(indent) + line;
        if (line.includes('{')) {
          result += '\n';
          indent++;
        }
        continue;
      }
      
      // Handle method declarations
      if (line.match(/^(public|private|protected|static|final|abstract|synchronized|native).*\([^)]*\)\s*(\{|throws)/)) {
        result += getIndentation(indent) + line;
        if (line.includes('{')) {
          result += '\n';
          indent++;
        }
        continue;
      }
      
      // Handle closing braces
      if (line === '}' || line.endsWith('}')) {
        indent = Math.max(0, indent - 1);
        result += getIndentation(indent) + line + '\n';
        continue;
      }
      
      // Handle opening braces
      if (line.includes('{')) {
        result += getIndentation(indent) + line + '\n';
        indent++;
        continue;
      }
      
      // Regular lines
      result += getIndentation(indent) + line + '\n';
      inAnnotation = false;
    }
    
    return result;
  };

  const applyBraceStyle = (code: string): string => {
    let result = code;
    
    switch (options.braceStyle) {
      case 'Allman':
        // Put opening braces on new line
        result = result.replace(/\s*\{\s*/g, '\n{\n');
        break;
      case 'GNU':
        // GNU style with extra indent
        result = result.replace(/\s*\{\s*/g, '\n  {\n');
        break;
      case 'Horstmann':
        // Horstmann style
        result = result.replace(/\s*\{\s*/g, '\n{ ');
        break;
      default: // K&R
        // Keep braces on same line
        if (options.insertSpaceBeforeBrace) {
          result = result.replace(/\s*\{\s*/g, ' {\n');
        } else {
          result = result.replace(/\s*\{\s*/g, '{\n');
        }
        break;
    }
    
    return result;
  };

  const addSpacing = (code: string): string => {
    let result = code;
    
    if (options.insertSpaceAfterComma) {
      result = result.replace(/,(?!\s)/g, ', ');
    }
    
    if (options.insertSpaceAfterKeyword) {
      result = result.replace(/\b(if|for|while|switch|catch|synchronized)\(/g, '$1 (');
    }
    
    if (options.insertSpaceAroundOperators) {
      result = result.replace(/([^=!<>])=([^=])/g, '$1 = $2');
      result = result.replace(/([^=!<>])==([^=])/g, '$1 == $2');
      result = result.replace(/([^=!<>])!=([^=])/g, '$1 != $2');
      result = result.replace(/([^<>])<=([^=])/g, '$1 <= $2');
      result = result.replace(/([^<>])>=([^=])/g, '$1 >= $2');
      result = result.replace(/([^<>])<([^<=])/g, '$1 < $2');
      result = result.replace(/([^<>])>([^>=])/g, '$1 > $2');
      result = result.replace(/([^+])\+([^+=])/g, '$1 + $2');
      result = result.replace(/([^-])-([^-=])/g, '$1 - $2');
      result = result.replace(/([^*])\*([^*=])/g, '$1 * $2');
      result = result.replace(/([^/])\/([^/=])/g, '$1 / $2');
      result = result.replace(/([^%])%([^=])/g, '$1 % $2');
    }
    
    return result;
  };

  const getIndentation = (level: number): string => {
    if (options.useTabs) {
      return '\t'.repeat(level);
    }
    return ' '.repeat(level * options.indentSize);
  };

  const getCodeStats = (): { lines: number; classes: number; methods: number; imports: number } => {
    if (!formattedCode) return { lines: 0, classes: 0, methods: 0, imports: 0 };
    
    const lines = formattedCode.split('\n').length;
    const classes = (formattedCode.match(/\b(class|interface|enum)\s+\w+/g) || []).length;
    const methods = (formattedCode.match(/\b(public|private|protected|static).*\([^)]*\)\s*\{/g) || []).length;
    const imports = (formattedCode.match(/^import\s+/gm) || []).length;
    
    return { lines, classes, methods, imports };
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(formattedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([formattedCode], { type: 'text/x-java-source' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'FormattedCode.java';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReset = (): void => {
    setInputCode('');
    setFormattedCode('');
  };

  const syntaxHighlight = (code: string): string => {
    if (!code) return '';
    
    return code
      // Java keywords
      .replace(/\b(public|private|protected|static|final|abstract|synchronized|native|strictfp|transient|volatile|class|interface|enum|extends|implements|import|package|new|this|super|null|true|false|if|else|for|while|do|switch|case|default|break|continue|return|try|catch|finally|throw|throws|instanceof|void|boolean|byte|short|int|long|float|double|char|String)\b/g,
        '<span style="color: #569cd6; font-weight: bold;">$1</span>')
      // Annotations
      .replace(/(@\w+)/g, '<span style="color: #4ec9b0;">$1</span>')
      // String literals
      .replace(/"(?:[^"\\]|\\.)*"/g, '<span style="color: #ce9178;">$&</span>')
      // Character literals
      .replace(/'(?:[^'\\]|\\.)*'/g, '<span style="color: #ce9178;">$&</span>')
      // Numbers
      .replace(/\b(\d+\.?\d*[fFdDlL]?)\b/g, '<span style="color: #b5cea8;">$1</span>')
      // Comments
      .replace(/(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, '<span style="color: #6a9955; font-style: italic;">$1</span>')
      // Method names
      .replace(/(\w+)\s*\(/g, '<span style="color: #dcdcaa;">$1</span>(')
      // Class names (capitalized words)
      .replace(/\b([A-Z][a-zA-Z0-9]*)\b/g, '<span style="color: #4ec9b0;">$1</span>')
      // Operators and punctuation
      .replace(/([{}[\]().,;:=+\-*/%<>!&|^~?])/g, '<span style="color: #d4d4d4;">$1</span>');
  };

  const updateOption = <K extends keyof JavaFormatterOptions>(key: K, value: JavaFormatterOptions[K]): void => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const stats = getCodeStats();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Coffee className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Java Code Formatter</h1>
                <p className="text-gray-600">Format and beautify your Java code with professional styling standards</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4 items-center">
              <button
                onClick={() => formatJava(inputCode)}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                Format Java
              </button>
              
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Options
              </button>
              
              <button
                onClick={() => setShowStats(!showStats)}
                className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors font-medium flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Stats
              </button>
              
              <button
                onClick={handleReset}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
            
            {showOptions && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-medium text-gray-900 mb-3">Java Formatting Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Indent Size:</label>
                    <select 
                      value={options.indentSize} 
                      onChange={(e) => updateOption('indentSize', Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={2}>2 spaces</option>
                      <option value={4}>4 spaces</option>
                      <option value={8}>8 spaces</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Brace Style:</label>
                    <select 
                      value={options.braceStyle} 
                      onChange={(e) => updateOption('braceStyle', e.target.value as JavaFormatterOptions['braceStyle'])}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="K&R">K&R</option>
                      <option value="Allman">Allman</option>
                      <option value="GNU">GNU</option>
                      <option value="Horstmann">Horstmann</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Max Line Length:</label>
                    <select 
                      value={options.maxLineLength} 
                      onChange={(e) => updateOption('maxLineLength', Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={80}>80</option>
                      <option value={100}>100</option>
                      <option value={120}>120</option>
                      <option value={140}>140</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="useTabs"
                      checked={options.useTabs}
                      onChange={(e) => updateOption('useTabs', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="useTabs" className="text-sm font-medium text-gray-700">Use tabs</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sortImports"
                      checked={options.sortImports}
                      onChange={(e) => updateOption('sortImports', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="sortImports" className="text-sm font-medium text-gray-700">Sort imports</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertSpaceAfterComma"
                      checked={options.insertSpaceAfterComma}
                      onChange={(e) => updateOption('insertSpaceAfterComma', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertSpaceAfterComma" className="text-sm font-medium text-gray-700">Space after comma</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertSpaceBeforeBrace"
                      checked={options.insertSpaceBeforeBrace}
                      onChange={(e) => updateOption('insertSpaceBeforeBrace', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertSpaceBeforeBrace" className="text-sm font-medium text-gray-700">Space before brace</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertSpaceAfterKeyword"
                      checked={options.insertSpaceAfterKeyword}
                      onChange={(e) => updateOption('insertSpaceAfterKeyword', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertSpaceAfterKeyword" className="text-sm font-medium text-gray-700">Space after keyword</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertSpaceAroundOperators"
                      checked={options.insertSpaceAroundOperators}
                      onChange={(e) => updateOption('insertSpaceAroundOperators', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertSpaceAroundOperators" className="text-sm font-medium text-gray-700">Space around operators</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="removeExtraBlankLines"
                      checked={options.removeExtraBlankLines}
                      onChange={(e) => updateOption('removeExtraBlankLines', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="removeExtraBlankLines" className="text-sm font-medium text-gray-700">Remove extra blank lines</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="alignParameters"
                      checked={options.alignParameters}
                      onChange={(e) => updateOption('alignParameters', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="alignParameters" className="text-sm font-medium text-gray-700">Align parameters</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="trimTrailingWhitespace"
                      checked={options.trimTrailingWhitespace}
                      onChange={(e) => updateOption('trimTrailingWhitespace', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="trimTrailingWhitespace" className="text-sm font-medium text-gray-700">Trim trailing whitespace</label>
                  </div>
                </div>
              </div>
            )}

            {showStats && formattedCode && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-medium text-blue-900 mb-3">Code Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.lines}</div>
                    <div className="text-blue-700">Lines</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.classes}</div>
                    <div className="text-blue-700">Classes/Interfaces</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.methods}</div>
                    <div className="text-blue-700">Methods</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.imports}</div>
                    <div className="text-blue-700">Imports</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Input Java Code</h2>
                <span className="text-sm text-gray-500">{inputCode.length} characters</span>
              </div>
              <textarea
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="Paste your Java code here..."
                className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Formatted Java Code</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    disabled={!formattedCode}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedCode}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
              <div className="relative">
                <pre className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm overflow-auto bg-gray-50">
                  <code 
                    dangerouslySetInnerHTML={{ 
                      __html: syntaxHighlight(formattedCode) || '<span class="text-gray-400">Formatted Java code will appear here...</span>' 
                    }}
                  />
                </pre>
              </div>
            </div>
          </div>
          
          {formattedCode && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Java formatting completed successfully</span>
                <span>{formattedCode.length} characters formatted</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JavaFormatter;